import type { Shortcut } from "@engram/core";
import { Book, BookCopy, FileText, GripVertical, Search, Tag, X } from "lucide-react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import { removeShortcut, reorderShortcuts } from "../db/actions";
import { useQuery } from "../db/client";
import { shortcutHref } from "./Shell";

export type ShortcutRow = Shortcut & { label: string | null };

/** All shortcuts in order, with the name of what each points to. */
export function useShortcuts(): ShortcutRow[] | undefined {
  return useQuery<ShortcutRow>(
    `SELECT s.*, COALESCE(
        (SELECT title FROM notes WHERE id = s.target_id AND deleted = 0),
        (SELECT name FROM notebooks WHERE id = s.target_id AND deleted = 0),
        (SELECT name FROM tags WHERE id = s.target_id AND deleted = 0),
        (SELECT name FROM saved_searches WHERE id = s.target_id AND deleted = 0)) AS label
     FROM shortcuts s WHERE s.deleted = 0 ORDER BY s.position`,
    [],
    ["shortcuts", "notes", "notebooks", "tags", "saved_searches"],
  );
}

export function ShortcutIcon({ kind, size = 14 }: { kind: string; size?: number }) {
  return kind === "note" ? <FileText size={size} /> : kind === "tag" ? <Tag size={size} /> : kind === "search" ? <Search size={size} /> : kind === "stack" ? <BookCopy size={size} /> : <Book size={size} />;
}

/** On the Shortcuts list (and the phone, which has no sidebar tree): notebooks, tags and searches above the notes. */
export function OtherShortcuts() {
  const rows = useShortcuts()?.filter((s) => s.kind !== "note");
  if (!rows?.length) return null;
  return (
    <div className="other-shortcuts">
      {rows.map((s) => (
        <Link key={s.id} href={shortcutHref(s.kind, s.target_id)} className="other-shortcut">
          <span className="menu-icon"><ShortcutIcon kind={s.kind} /></span>
          <span className="menu-label">{s.label || "Untitled"}</span>
        </Link>
      ))}
    </div>
  );
}

/** Reorder (drag the handle, or arrow keys on it) and remove shortcuts. Pointer events, so it works by touch too. */
export function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  const rows = useShortcuts() ?? [];
  const [order, setOrder] = useState<string[] | null>(null);
  const [drag, setDrag] = useState<{ id: string; offset: number } | null>(null);
  const start = useRef<{ y: number; h: number; index: number; ids: string[] } | null>(null);
  const byId = new Map(rows.map((r) => [r.id, r]));
  const list = (order ?? rows.map((r) => r.id)).flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []));

  const move = (ids: string[], id: string, to: number) => {
    const next = ids.filter((x) => x !== id);
    next.splice(Math.max(0, Math.min(next.length, to)), 0, id);
    return next;
  };
  const commit = (ids: string[]) => {
    setOrder(ids);
    void reorderShortcuts(ids);
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal shortcuts-dialog" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <h2>Shortcuts</h2>
        <p className="muted small">Drag to reorder. Ctrl+1…9 open the first nine.</p>
        <div className="sc-list">
          {list.map((s, i) => (
            <div key={s.id} className={`sc-row${drag?.id === s.id ? " dragging" : ""}`} style={drag?.id === s.id ? { transform: `translateY(${drag.offset}px)` } : undefined}>
              <button
                className="sc-handle"
                aria-label={`Move ${s.label || "Untitled"}`}
                title="Drag, or use the arrow keys"
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  const h = (e.currentTarget.closest(".sc-row") as HTMLElement).offsetHeight;
                  start.current = { y: e.clientY, h, index: i, ids: list.map((r) => r.id) };
                  setDrag({ id: s.id, offset: 0 });
                }}
                onPointerMove={(e) => {
                  const st = start.current;
                  if (!st || drag?.id !== s.id) return;
                  const dy = e.clientY - st.y;
                  const to = Math.max(0, Math.min(st.ids.length - 1, st.index + Math.round(dy / st.h)));
                  setOrder(move(st.ids, s.id, to));
                  setDrag({ id: s.id, offset: dy - (to - st.index) * st.h });
                }}
                onPointerUp={() => {
                  if (start.current && order) commit(order);
                  start.current = null;
                  setDrag(null);
                }}
                onPointerCancel={() => {
                  start.current = null;
                  setDrag(null);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                  e.preventDefault();
                  commit(move(list.map((r) => r.id), s.id, i + (e.key === "ArrowUp" ? -1 : 1)));
                  const btn = e.currentTarget;
                  requestAnimationFrame(() => btn.focus());
                }}
              >
                <GripVertical size={15} />
              </button>
              <span className="sc-num">{i < 9 ? i + 1 : ""}</span>
              <span className="menu-icon"><ShortcutIcon kind={s.kind} /></span>
              <span className="menu-label">{s.label || "Untitled"}</span>
              <button className="icon-btn" aria-label={`Remove ${s.label || "Untitled"} from Shortcuts`} title="Remove from Shortcuts" onClick={() => void removeShortcut(s.kind, s.target_id)}>
                <X size={14} />
              </button>
            </div>
          ))}
          {rows.length === 0 && <p className="muted">No shortcuts yet. Add them from the ··· menu of a note, notebook, tag or saved search.</p>}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
